import { useEffect, useRef, useState } from "react";
import {
  ArrowLeft,
  Copy,
  UsersThree,
  Trophy,
  Check,
  ShareNetwork,
} from "@phosphor-icons/react";
import { api, money, pct, signed, labels, hints, cn } from "./api";
import {
  freshEffects,
  readCandles,
  rematchStep,
  writeCandles,
} from "./battle-state";
import { Chart } from "./Chart";
import { ChoiceSpectrum, spectrumEdges } from "./ChoiceSpectrum";
import { Confetti, CountUp } from "./effects";
import { PositionTrack } from "./PositionTrack";
import type { Extreme } from "./PositionTrack";
import { ResultExplanation } from "./ResultExplanation";
import type { FollowUp } from "./ResultExplanation";
import "./battle.css";

type Bar = {
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
};
type Question = {
  id: string;
  market: string;
  sector: string;
  context: string;
  horizon: number;
  past: Bar[];
  future?: Bar[];
  company?: string;
  date?: string;
  explanation?: string;
  briefExplanation?: string;
  invalid?: boolean;
  followUp?: FollowUp;
  outcomes?: { action: string; rate: number }[];
};
type Result = {
  action: string;
  automatic: boolean;
  before: number;
  after: number;
  profit: number;
  rate?: number;
  best?: Edge;
  worst?: Edge;
};
type Edge = { action: string; profit: number };
type Effect = { id: number; player: string; reaction: string };
type Burst = {
  key: number;
  emoji: string;
  name: string;
  left: number;
  mine: boolean;
};
type Player = {
  id: string;
  name: string;
  host: boolean;
  ready: boolean;
  online: boolean;
  answered: boolean;
  assets: number;
  profit: number;
  rank: number;
  results: Result[];
  reactions?: (string | null)[];
  questionReactions?: (string | null)[];
  rematch?: boolean;
};
type Room = {
  code: string;
  state: string;
  count: number;
  capacity: number;
  canJoin: boolean;
  serverTime: number;
  version: number;
  matchId?: string;
  me?: string;
  players?: Player[];
  index?: number;
  deadline?: number | null;
  myAction?: string | null;
  current?: Question | null;
  revealed?: Question[];
  next?: string | null;
  corrected?: boolean;
  effects?: Effect[];
  extremes?: { best: Extreme; worst: Extreme } | null;
};

const REACTIONS = [
  { id: "laugh", emoji: "😂", label: "웃음" },
  { id: "cry", emoji: "😭", label: "울음" },
  { id: "surprise", emoji: "😮", label: "놀람" },
  { id: "clap", emoji: "👏", label: "박수" },
];

const QUESTION_REACTIONS = [
  { id: "hurry", emoji: "⏰", label: "빨리빨리~" },
  { id: "thinking", emoji: "🤔", label: "고민 중!" },
  { id: "wait", emoji: "✋", label: "잠깐만~" },
  { id: "cheer", emoji: "💪", label: "가보자!" },
  ...REACTIONS,
];

const BURST_MS = 2600;
export function Battle({
  code,
  navigate,
  defaultName,
}: {
  code: string;
  navigate: (path: string) => void;
  defaultName: string;
}) {
  const [room, setRoom] = useState<Room | null>(null);
  const [name, setName] = useState(defaultName);
  const [error, setError] = useState("");
  const [syncError, setSyncError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);
  const [remaining, setRemaining] = useState(0);
  const [candles, setCandles] = useState(() => readCandles(() => localStorage));
  const [bursts, setBursts] = useState<Burst[]>([]);
  const received = useRef({ server: 0, local: 0, deadline: 0 });
  const version = useRef(-1);
  const pending = useRef(false);
  const lastEffect = useRef<number | null>(null);
  const burstKey = useRef(0);
  const burstTimers = useRef(new Set<ReturnType<typeof setTimeout>>());
  const awaitedRematch = useRef(false);
  const chooseCandles = (value: boolean) => {
    setCandles(value);
    writeCandles(() => localStorage, value);
  };
  const burst = (reaction: string, name: string, mine: boolean) => {
    const emoji = QUESTION_REACTIONS.find((r) => r.id === reaction)?.emoji;
    if (!emoji) return;
    const key = ++burstKey.current;
    setBursts((list) => [
      ...list.slice(-11),
      { key, emoji, name, mine, left: 8 + Math.random() * 72 },
    ]);
    const timer = setTimeout(() => {
      burstTimers.current.delete(timer);
      setBursts((list) => list.filter((b) => b.key !== key));
    }, BURST_MS);
    burstTimers.current.add(timer);
  };
  const accept = (value: Room) => {
    if (value.version < version.current) return;
    version.current = value.version;
    const effects = freshEffects(
      lastEffect.current,
      value.effects || [],
      value.me,
    );
    lastEffect.current = effects.seen;
    for (const e of effects.fresh)
      burst(
        e.reaction,
        value.players?.find((p) => p.id === e.player)?.name || "",
        false,
      );
    received.current = {
      server: value.serverTime,
      local: performance.now(),
      deadline: value.deadline || 0,
    };
    setRemaining(
      Math.max(0, Math.ceil(((value.deadline || 0) - value.serverTime) / 1000)),
    );
    setRoom(value);
  };
  useEffect(() => {
    if (!code) return;
    let stopped = false;
    let timer: ReturnType<typeof setTimeout>;
    const poll = async () => {
      try {
        const value = await api<Room>(`/battles/${encodeURIComponent(code)}`);
        if (!stopped) {
          accept(value);
          setSyncError("");
        }
      } catch (e) {
        if (!stopped) setSyncError((e as Error).message);
      }
      if (!stopped) timer = setTimeout(poll, document.hidden ? 10000 : 1000);
    };
    void poll();
    const tick = setInterval(() => {
      const t = received.current;
      setRemaining(
        Math.max(
          0,
          Math.ceil(
            (t.deadline - t.server - (performance.now() - t.local)) / 1000,
          ),
        ),
      );
    }, 200);
    const timers = burstTimers.current;
    return () => {
      stopped = true;
      clearTimeout(timer);
      clearInterval(tick);
      timers.forEach(clearTimeout);
    };
  }, [code]);
  useEffect(() => {
    if (
      room?.state &&
      ["countdown", "question", "result", "completed"].includes(room.state)
    ) {
      window.scrollTo({ top: 0, behavior: "instant" });
    }
  }, [room?.state, room?.index]);
  const act = async (action: string, body: unknown = {}) => {
    if (pending.current) return;
    pending.current = true;
    setBusy(true);
    setError("");
    try {
      const value = await api<Room>(
        code ? `/battles/${encodeURIComponent(code)}/${action}` : "/battles",
        body,
      );
      if (!code) navigate(`/battle/${value.code}`);
      else if (action === "leave") navigate("/");
      else if (action === "rematch" && value.next)
        navigate(`/battle/${value.next}`);
      else accept(value);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      pending.current = false;
      setBusy(false);
    }
  };
  const share = async (copy = false) => {
    const url = `${location.origin}/battle/${code}`;
    try {
      if (!copy && navigator.share)
        await navigator.share({
          title: "개미톡 친구 대결",
          text: "같은 5문제, 누가 더 잘 투자할까? 함께 대결해요.",
          url,
        });
      else {
        await navigator.clipboard.writeText(url);
        setNotice("초대 링크를 복사했어요.");
      }
    } catch (e) {
      if ((e as Error).name !== "AbortError")
        setError("공유하지 못했어요. 주소창의 링크를 복사해 주세요.");
    }
  };
  const me = room?.players?.find((p) => p.id === room.me);
  const index = room?.index || 0;
  const rematchState = room?.state || "";
  const rematchNext = room?.next;
  const rematchRequested = !!me?.rematch;
  useEffect(() => {
    const step = rematchStep(awaitedRematch.current, {
      state: rematchState,
      next: rematchNext,
      requested: rematchRequested,
    });
    awaitedRematch.current = step.awaiting;
    // Joining through the finished room seats us before we move to the lobby.
    if (step.join) void act("rematch");
  }, [rematchState, rematchNext, rematchRequested]);
  const sendReaction = (phase: "question" | "result", reaction: string) => {
    if (!room || !me) return;
    burst(reaction, me.name, true);
    // A press that lands just after the phase changed is simply dropped.
    api<Room>(`/battles/${encodeURIComponent(code)}/react`, {
      phase,
      matchId: room.matchId,
      index,
      reaction,
    }).then(accept, () => {});
  };
  const chartToggle = (
    <div className="segmented small battle-chart-toggle">
      <button
        className={!candles ? "selected" : ""}
        aria-pressed={!candles}
        onClick={() => chooseCandles(false)}
      >
        선
      </button>
      <button
        className={candles ? "selected" : ""}
        aria-pressed={candles}
        onClick={() => chooseCandles(true)}
      >
        캔들·거래량
      </button>
    </div>
  );
  const live = room && ["countdown", "question", "result"].includes(room.state);
  const nicknameForm = (joining: boolean) => (
    <form
      className="battle-entry"
      onSubmit={(e) => {
        e.preventDefault();
        void act(joining ? "join" : "create", { name });
      }}
    >
      <label htmlFor="battle-name">대결에서 사용할 닉네임</label>
      <input
        id="battle-name"
        value={name}
        minLength={2}
        maxLength={24}
        required
        autoComplete="nickname"
        onChange={(e) => setName(e.target.value)}
        placeholder="2~12자 닉네임"
      />
      <button className="btn primary" disabled={busy}>
        {joining ? "대결방 입장하기" : "초대할 방 만들기"}
      </button>
      <p className="caption">
        로그인 없이 참여해요. 같은 브라우저에서 링크를 열면 돌아올 수 있어요.
      </p>
    </form>
  );
  const reactions = (phase: "question" | "result") => {
    const field = phase === "question" ? "questionReactions" : "reactions";
    const options = phase === "question" ? QUESTION_REACTIONS : REACTIONS;
    return (
      <>
        <div
          className="battle-reaction-buttons"
          aria-label={phase === "question" ? "풀이 중 리액션" : "결과 리액션"}
        >
          {options.map((reaction) => (
            <button
              key={reaction.id}
              className="btn outline"
              aria-label={`${reaction.label} 보내기`}
              disabled={remaining === 0}
              onClick={() => sendReaction(phase, reaction.id)}
            >
              <span aria-hidden="true">{reaction.emoji}</span>
              {phase === "question" && (
                <span className="reaction-label">{reaction.label}</span>
              )}
            </button>
          ))}
        </div>
        <p className="caption" aria-live="polite">
          {room?.players
            ?.filter((p) => p[field]?.[index])
            .map(
              (p) =>
                `${p.name} ${options.find((r) => r.id === p[field]?.[index])?.label || ""}`,
            )
            .join(" · ") || "누를 때마다 친구들 화면에 리액션이 떠올라요."}
        </p>
      </>
    );
  };
  const standings = (final = false) => (
    <div
      className="battle-standings"
      aria-label={final ? "최종 순위" : "중간 순위"}
    >
      {room?.players?.map((p, i) => (
        <div
          className={`battle-standing ${p.id === room.me ? "is-me" : ""}`}
          style={{ animationDelay: `${i * 90}ms` }}
          key={p.id}
        >
          <span className="battle-rank">
            {final && p.rank === 1 ? "👑" : p.rank}
          </span>
          <span>
            <strong>
              {p.name}
              {p.id === room.me && " · 나"}
            </strong>
            <small className={cn(p.profit)}>
              {pct((p.profit / 100_000_000) * 100)}
            </small>
          </span>
          <strong>{money(p.assets)}</strong>
        </div>
      ))}
    </div>
  );
  const edges = (i: number) =>
    spectrumEdges(
      (room?.revealed?.[i]?.outcomes || []).map((o) => ({
        action: o.action,
        value: o.rate,
      })),
    );
  const choices = (i: number) => {
    const edge = edges(i);
    return (
      <ul className="battle-choices" aria-label="친구들의 선택과 이번 손익">
        {room?.players?.map((p, n) => {
          const r = p.results[i];
          return (
            r && (
              <li
                key={p.id}
                className={p.id === room?.me ? "is-me" : ""}
                style={{ animationDelay: `${n * 110}ms` }}
              >
                <strong className="battle-choice-name">
                  {p.name}
                  {p.id === room?.me && <small>나</small>}
                </strong>
                <span className="battle-choice-action" data-action={r.action}>
                  {labels[r.action]}
                  {r.automatic && <small>자동 선택</small>}
                </span>
                {edge?.best.action === r.action && (
                  <span className="battle-choice-badge is-best">
                    최고의 선택
                  </span>
                )}
                {edge?.worst.action === r.action && (
                  <span className="battle-choice-badge is-worst">
                    최악의 선택
                  </span>
                )}
                <span className="caption">이번 문제 손익</span>
                <strong className={`battle-choice-profit ${cn(r.profit)}`}>
                  {signed(r.profit)}
                </strong>
              </li>
            )
          );
        })}
      </ul>
    );
  };
  // Every possible choice from worst to best, with each player placed on it.
  const spectrum = (i: number) => {
    const mine = me?.results[i];
    return (
      <ChoiceSpectrum
        outcomes={(room?.revealed?.[i]?.outcomes || []).map((o) => ({
          action: o.action,
          value: o.rate,
        }))}
        mine={mine?.action}
        format={(rate) => pct(rate * 100)}
        extra={(action) => (
          <span className="choice-spectrum-players">
            {room?.players
              ?.filter((p) => p.results[i]?.action === action)
              .map((p) => (
                <em key={p.id} className={p.id === room?.me ? "is-me" : ""}>
                  {p.name}
                </em>
              ))}
          </span>
        )}
        footer={
          mine?.best &&
          mine.worst && (
            <p className="caption">
              내 자산 기준 · 최고의 선택이었다면{" "}
              <strong className={cn(mine.best.profit)}>
                {signed(mine.best.profit)}
              </strong>
              , 최악의 선택이었다면{" "}
              <strong className={cn(mine.worst.profit)}>
                {signed(mine.worst.profit)}
              </strong>
            </p>
          )
        }
      />
    );
  };
  const position = (extremes: { best: Extreme; worst: Extreme }) => (
    <PositionTrack
      best={extremes.best}
      worst={extremes.worst}
      players={(room?.players || []).map((p) => ({
        id: p.id,
        name: p.name,
        assets: p.assets,
        rank: p.rank,
        me: p.id === room?.me,
      }))}
    />
  );
  const myProfit = me?.results[index]?.profit || 0;
  const host = room?.players?.find((p) => p.host);
  const canOpenRematch = !!me?.host || !host?.online;
  const rematchRequesters =
    room?.players?.filter((p) => p.rematch && p.id !== room.me) || [];
  return (
    <div
      className={`battle-page ${room && ["question", "result", "completed"].includes(room.state) ? "in-game" : ""}`}
    >
      <div className="battle-top">
        <button
          className="text-button"
          onClick={() => {
            if (room?.me) void act("leave");
            else navigate("/");
          }}
          disabled={busy}
        >
          <ArrowLeft size={18} />
          {live ? "나가기 · 진행 유지" : "나가기"}
        </button>
        <span className="quiet-chip">
          <UsersThree size={17} />
          친구 대결
        </span>
      </div>
      {syncError && (
        <div role="alert" className="error-banner">
          연결을 확인하고 있어요. {syncError}
        </div>
      )}
      {error && (
        <div role="alert" className="error-banner">
          {error}
        </div>
      )}
      {notice && <p role="status">{notice}</p>}
      <div className="battle-bursts" aria-hidden="true">
        {bursts.map((b) => (
          <span
            key={b.key}
            className={b.mine ? "is-mine" : ""}
            style={{ left: `${b.left}%` }}
          >
            <i>{b.emoji}</i>
            <small>{b.mine ? "나" : b.name}</small>
          </span>
        ))}
      </div>
      {!code && (
        <section className="panel battle-welcome">
          <span className="eyebrow">함께 고르고, 함께 확인하는</span>
          <h1>친구와 투자 한 판</h1>
          <p>
            같은 5문제, 같은 1억 원.
            <br />
            마지막에 더 큰 자산을 만든 친구는 누구일까요?
          </p>
          <div className="battle-rules">
            <span>2~4명</span>
            <span>문제당 60초</span>
            <span>월간 랭킹과 별도</span>
          </div>
          {nicknameForm(false)}
        </section>
      )}
      {code && !room && <p role="status">대결방을 불러오는 중이에요…</p>}
      {room && !room.me && (
        <section className="panel battle-welcome">
          <h1>친구가 대결에 초대했어요</h1>
          <p>같은 5문제를 함께 풀고 최종 자산으로 경쟁해요.</p>
          <p>
            참가자 {room.count}/{room.capacity}명 · 문제당 60초
          </p>
          {room.canJoin ? (
            nicknameForm(true)
          ) : (
            <>
              <p>
                {room.state === "expired"
                  ? "만료된 대기방이에요."
                  : room.state === "waiting"
                    ? "참가 인원이 가득 찼어요."
                    : "이미 시작했거나 종료된 대결이에요. 참가했던 브라우저에서 다시 열어 주세요."}
              </p>
              <button
                className="btn primary"
                onClick={() => navigate("/battle")}
              >
                새 대결방 만들기
              </button>
            </>
          )}
        </section>
      )}
      {room?.me && room.state === "waiting" && (
        <section className="panel battle-lobby">
          <span className="eyebrow">대결 대기방</span>
          <h1>친구들이 모이면 시작해요</h1>
          <p>2~4명 · 5문제 · 1억 원 시작 · 미응답은 보유 유지</p>
          <div className="battle-share">
            <button className="btn outline" onClick={() => void share()}>
              <ShareNetwork size={18} />
              친구 초대
            </button>
            <button className="btn outline" onClick={() => void share(true)}>
              <Copy size={18} />
              링크 복사
            </button>
          </div>
          <ul className="battle-members">
            {room.players?.map((p) => (
              <li key={p.id}>
                <span>
                  <strong>{p.name}</strong>
                  {p.id === room.me && " · 나"}
                  {p.host && <small>방장</small>}
                </span>
                <span>
                  {me?.host && !p.online && p.id !== room.me && (
                    <button
                      className="text-button"
                      disabled={busy}
                      onClick={() => void act("remove", { playerId: p.id })}
                      aria-label={`${p.name} 대기방에서 제외`}
                    >
                      제외하기
                    </button>
                  )}
                  {!p.online
                    ? "연결 끊김"
                    : p.host
                      ? "진행 담당"
                      : p.ready
                        ? "준비 완료"
                        : "준비 중"}
                </span>
              </li>
            ))}
          </ul>
          <p className="caption">
            {room.count}/{room.capacity}명 · 대기방 만료까지{" "}
            {Math.ceil(remaining / 60)}분
          </p>
          {me?.host ? (
            <button
              className="btn primary"
              disabled={
                busy ||
                room.count < 2 ||
                room.players?.some((p) => !p.online || (!p.host && !p.ready))
              }
              onClick={() => void act("start")}
            >
              함께 시작하기
            </button>
          ) : (
            <button
              className={`btn ${me?.ready ? "outline" : "primary"}`}
              disabled={busy}
              onClick={() => void act("ready", { ready: !me?.ready })}
            >
              {me?.ready ? "준비 취소" : "준비 완료"}
            </button>
          )}
          <p className="caption">
            모두 준비하면 방장이 시작할 수 있어요. 방장 연결이 끊기면 60초 뒤
            다른 친구에게 넘어가요.
          </p>
        </section>
      )}
      {room?.me && room.state === "countdown" && (
        <section className="panel battle-countdown">
          <span className="eyebrow">모두 같은 출발선에서</span>
          <h1>잠시 후 시작해요</h1>
          <strong role="timer" key={remaining}>
            {remaining || "시작!"}
          </strong>
          <p>결과는 모두 선택한 뒤 함께 공개해요.</p>
        </section>
      )}
      {room?.me && room.state === "question" && room.current && (
        <>
          <div className="battle-stage">
            <h1 className="battle-question-progress">{index + 1} / 5 문제</h1>
            <span
              className={`battle-timer ${remaining <= 10 ? "urgent" : ""} ${remaining <= 5 && remaining > 0 ? "last-seconds" : ""}`}
              role="timer"
            >
              {remaining}초
            </span>
          </div>
          <div
            className={`battle-time-track ${remaining <= 10 ? "urgent" : ""}`}
            aria-hidden="true"
          >
            <span
              style={{ width: `${Math.min(100, (remaining / 60) * 100)}%` }}
            />
          </div>
          {remaining <= 10 && (
            <p className="battle-time-hint" role="status">
              {remaining <= 5
                ? "곧 선택이 마감돼요!"
                : "마지막 10초! 선택을 확인해 주세요."}
            </p>
          )}
          <div className="battle-game" key={`${room.matchId}-${index}`}>
            <section className="panel">
              <div className="battle-panel-head">
                <span className="eyebrow">
                  {room.current.market === "KR" ? "국내" : "미국"} ·{" "}
                  {room.current.sector}
                </span>
                {chartToggle}
              </div>
              <Chart
                key={`${room.matchId}-${index}-question`}
                past={room.current.past}
                horizon={room.current.horizon}
                candles={candles}
                playback
              />
              <p className="caption">
                시작점 100 기준 · {room.current.horizon}거래일 후 정산
              </p>
              <p className="battle-context">{room.current.context}</p>
            </section>
            <section className="panel battle-decision">
              <span>나의 자산</span>
              <h2>{money(me?.assets || 100_000_000)}</h2>
              <p>모두 고르기 전까지 선택을 바꿀 수 있어요.</p>
              <div className="battle-actions">
                {Object.entries(labels).map(([action, label]) => (
                  <button
                    key={action}
                    className={`btn ${room.myAction === action ? "primary is-picked" : "outline"}`}
                    disabled={
                      busy || room.myAction === action || remaining === 0
                    }
                    onClick={() =>
                      void act("answer", {
                        matchId: room.matchId,
                        index,
                        action,
                      })
                    }
                  >
                    {room.myAction === action && <Check size={18} />}
                    <span className="battle-action-label">
                      {label}
                      <small>{hints[action]}</small>
                    </span>
                  </button>
                ))}
              </div>
              <p role="status">
                {room.myAction
                  ? "선택 완료! 친구들이 고르는 동안 바꿀 수 있어요."
                  : "시간 내 미응답 시 ‘보유 유지’로 자동 선택해요."}
              </p>
              <p className="battle-answer-count" role="status">
                선택 완료{" "}
                <strong>
                  {room.players?.filter((p) => p.answered).length}/{room.count}
                  명
                </strong>
              </p>
              <ul className="battle-members compact battle-answer-members">
                {room.players?.map((p) => (
                  <li key={p.id} className={p.answered ? "has-answered" : ""}>
                    <strong>
                      {p.name}
                      {p.id === room.me ? " · 나" : ""}
                    </strong>
                    <span
                      key={`${index}-${p.answered}`}
                      className={`battle-answer-status ${p.answered ? "is-answered" : "is-thinking"}`}
                    >
                      {p.answered ? (
                        <Check size={18} weight="bold" />
                      ) : (
                        <span
                          className="battle-thinking-dot"
                          aria-hidden="true"
                        />
                      )}
                      {p.answered ? "선택 완료!" : "고르는 중"}
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          </div>
          <section className="panel battle-reactions">
            <h2>친구에게 한마디</h2>
            {reactions("question")}
          </section>
        </>
      )}
      {room?.me && room.state === "result" && (
        <>
          <div className="battle-stage">
            <h1>{index + 1}번째 선택의 결과</h1>
            <span className="battle-timer" role="timer">
              {remaining}초 후 {index === 4 ? "최종 결과" : "다음 문제"}
            </span>
          </div>
          <section
            className="panel battle-result"
            key={`${room.matchId}-${index}`}
          >
            <div className="battle-panel-head">
              <h2>{room.revealed?.[index]?.company}</h2>
              {chartToggle}
            </div>
            {room.revealed?.[index] && (
              <Chart
                key={`${room.matchId}-${index}-result`}
                playback
                animate
                candles={candles}
                past={room.revealed[index].past}
                future={room.revealed[index].future}
              />
            )}
            <div className={`battle-verdict is-${cn(myProfit)}`}>
              {myProfit > 0 && <Confetti />}
              <span className="battle-verdict-emoji" aria-hidden="true">
                {myProfit > 0 ? "🎉" : myProfit < 0 ? "😵" : "😐"}
              </span>
              <span>
                나의 이번 손익
                <strong className={cn(myProfit)}>
                  <CountUp value={myProfit} />
                </strong>
              </span>
              <small>
                주가 {pct((me?.results[index]?.rate || 0) * 100)} ·{" "}
                {labels[me?.results[index]?.action || "hold"]}
              </small>
            </div>
            <h3 className="battle-choices-title">
              최고의 선택과 최악의 선택 사이, 우리는 어디쯤?
            </h3>
            {spectrum(index)}
            <h3 className="battle-choices-title">우리는 이렇게 골랐어요</h3>
            {choices(index)}
            <ResultExplanation
              explanation={
                room.revealed?.[index]?.briefExplanation ||
                room.revealed?.[index]?.explanation ||
                ""
              }
              followUp={room.revealed?.[index]?.followUp}
            />
          </section>
          <section className="panel battle-reactions">
            <h2>이번 결과, 어떤 기분이에요?</h2>
            {reactions("result")}
            {me?.host && (
              <button
                className="btn primary full"
                disabled={busy || remaining === 0}
                onClick={() =>
                  void act("next", { matchId: room.matchId, index })
                }
              >
                {index === 4 ? "최종 결과 보기" : "다음 문제"}
              </button>
            )}
            <p className="caption">
              방장이 다음으로 넘기거나, 시간이 끝나면 자동으로 진행해요.
            </p>
          </section>
          <section className="panel">
            <h2>중간 순위</h2>
            {standings()}
          </section>
        </>
      )}
      {room?.me && room.state === "completed" && (
        <>
          <section className="panel battle-finish">
            {me?.rank === 1 && <Confetti count={48} />}
            <Trophy className="battle-trophy" size={40} weight="duotone" />
            <span className="eyebrow">5번의 선택, 우리의 결과</span>
            <h1>나는 {me?.rank}위!</h1>
            <p>
              최종 자산 {money(me?.assets || 0)} ·{" "}
              {pct(((me?.profit || 0) / 100_000_000) * 100)}
            </p>
            {standings(true)}
            {!room.next && rematchRequesters.length > 0 && (
              <p className="battle-rematch-requests" role="status">
                🙌 {rematchRequesters.map((p) => p.name).join(", ")}님이
                재대결을 원해요!
              </p>
            )}
            {room.next || canOpenRematch ? (
              <button
                className="btn primary"
                disabled={busy}
                onClick={() => void act("rematch")}
              >
                {room.next
                  ? "재대결 대기방으로"
                  : rematchRequesters.length
                    ? "재대결 시작하기"
                    : "같은 친구들과 재대결"}
              </button>
            ) : (
              <button
                className={`btn ${me?.rematch ? "outline" : "primary"}`}
                disabled={busy || me?.rematch}
                onClick={() => void act("rematch")}
              >
                {me?.rematch
                  ? "재대결 요청 완료 · 방장을 기다리는 중"
                  : "방장에게 재대결 요청"}
              </button>
            )}
            <p className="caption">
              {room.next || canOpenRematch
                ? "새로운 5문제로 시작해요. 방장은 그대로 유지되고, 기존 닉네임으로 바로 입장해요."
                : "방장이 재대결을 열면 기존 닉네임으로 자동 입장해요. 방장이 나가면 직접 방을 열 수 있어요."}
            </p>
          </section>
          <section className="panel">
            <div className="battle-panel-head">
              <h2>어느 선택에서 갈렸을까요?</h2>
              {chartToggle}
            </div>
            {room.revealed?.map((q, i) => {
              const edge = edges(i);
              return (
                <article className="battle-review" key={q.id}>
                  <header>
                    <strong>
                      {i + 1}. {q.company}
                    </strong>
                    <span>
                      {q.date} · 주가{" "}
                      <b className={cn(me?.results[i]?.rate || 0)}>
                        {pct((me?.results[i]?.rate || 0) * 100)}
                      </b>
                      {q.invalid && " · 무효 문제"}
                    </span>
                  </header>
                  {edge && (
                    <p className="battle-review-edges">
                      <span className="battle-choice-badge is-best">
                        최고 · {labels[edge.best.action]}{" "}
                        {pct(edge.best.value * 100)}
                      </span>
                      <span className="battle-choice-badge is-worst">
                        최악 · {labels[edge.worst.action]}{" "}
                        {pct(edge.worst.value * 100)}
                      </span>
                    </p>
                  )}
                  {choices(i)}
                  <details>
                    <summary>차트와 설명 보기</summary>
                    <Chart past={q.past} future={q.future} candles={candles} />
                    <p className="battle-context">{q.explanation}</p>
                  </details>
                </article>
              );
            })}
          </section>
          {room.extremes && (
            <section className="panel">
              <h2>우리는 어디쯤 왔을까요?</h2>
              {position(room.extremes)}
            </section>
          )}
        </>
      )}
      {room?.me && room.state === "expired" && (
        <section className="panel">
          <h1>대기방이 만료됐어요</h1>
          <p>새 방을 만들어 친구들을 다시 초대해 주세요.</p>
          <button className="btn primary" onClick={() => navigate("/battle")}>
            새 대결방 만들기
          </button>
        </section>
      )}
      {room?.corrected && (
        <p role="status" className="battle-correction">
          출제 오류가 있는 문제는 수익률 0%로 무효 처리하고 전체 성적을 다시
          계산했어요.
        </p>
      )}
    </div>
  );
}
